import {
  BundleEntry,
  Condition,
  List,
  Observation,
  Patient,
  Practitioner,
  Reference,
} from "@medplum/fhirtypes";

import { Systems } from "@/fhir/enums/systems";
import { generateFullUrl } from "@/utils/medplum";

import { PREGNANCY_RECORD_CODING } from "../constants/record";

// --- Build -------------------------------------------------------------------

/**
 * Constructs a bare pregnancy record `List` resource (no bundle entry wrapper).
 *
 * @param patientReference - Patient reference object (e.g. `{ reference: "Patient/123" }`)
 * @param onsetDateTime - ISO datetime string for when the pregnancy began; stored as `List.date`
 * @param authorReference - Optional reference to the author stored as `List.source`
 * @returns A new `List` resource with an empty `entry` array, ready to have items pushed on to it
 */
export function buildPregnancyRecordList(
  patientReference: Reference<Patient>,
  onsetDateTime: string,
  authorReference: Reference<Practitioner | Patient> | undefined,
): List {
  return {
    resourceType: "List",
    status: "current",
    mode: "working",
    subject: patientReference,
    date: onsetDateTime,
    code: { coding: [PREGNANCY_RECORD_CODING] },
    entry: [],
    source: authorReference,
  };
}

/**
 * Builds a `BundleEntry` containing a pregnancy `Condition` (SNOMED 77386006)
 * for use in a FHIR transaction bundle.
 *
 * @param patientReferenceString - Patient reference string (e.g. `"Patient/123"`)
 * @param onsetDateTime - ISO datetime string for when the pregnancy began
 * @param options.request - Optional bundle entry request override; defaults to `POST Condition`
 * @returns `BundleEntry<Condition>` containing the pregnancy `Condition`
 */
export function buildPregnancyConditionBundleEntry(
  patientReferenceString: string,
  onsetDateTime: string,
  { resource, request }: { resource?: Partial<Condition>; request?: BundleEntry["request"] } = {},
): BundleEntry<Condition> {
  return {
    fullUrl: generateFullUrl(),
    resource: {
      resourceType: "Condition",
      code: { coding: [PREGNANCY_RECORD_CODING] },
      subject: { reference: patientReferenceString },
      clinicalStatus: {
        coding: [{ system: Systems.HL7ConditionClinical, code: "active", display: "Active" }],
      },
      onsetDateTime,
      ...resource,
    },
    request: {
      method: "POST",
      url: "Condition",
      ...request,
    },
  };
}

// --- Get ---------------------------------------------------------------------

/**
 * Gets the pregnancy `Condition` reference from a pregnancy `List`.
 *
 * @param list - FHIR `List` resource representing the pregnancy record
 * @returns The pregnancy `Condition` reference, or `undefined` if none is found
 */
export function getPregnancyConditionReferenceFromPregnancyRecord(
  list: List,
): Reference<Condition> | undefined {
  return list.entry?.find((entry) =>
    entry.flag?.coding?.some(
      (coding) =>
        coding.system === PREGNANCY_RECORD_CODING.system &&
        coding.code === PREGNANCY_RECORD_CODING.code,
    ),
  )?.item as Reference<Condition> | undefined;
}

// --- Update ------------------------------------------------------------------

/**
 * Appends bundle entries to a pregnancy record `List` as item references.
 *
 * Each entry is registered under `List.entry` with its `fullUrl` as the item
 * reference, the resource's `code` as the flag, and the provided date. This
 * allows the observations and conditions to be retrieved later via
 * `_has:List:item:_id` searches.
 *
 * @param list - Pregnancy record `List` to append to; mutated in place
 * @param items - Bundle entries whose `fullUrl` and `resource.code` are used
 * @param date - ISO datetime string stored as `List.entry[].date`
 */
export function pushToPregnancyRecord(
  list: List,
  items: BundleEntry<Observation | Condition>[],
  date: string,
) {
  list.entry!.push(
    ...items.map((item) => ({
      item: { reference: item.fullUrl },
      flag: item.resource?.code,
      date,
    })),
  );
}

/**
 * Returns the `If-Match` value for a bundle entry that writes `list`, built from the version it was
 * read at. A write based on an older read then fails with 412 instead of overwriting a newer
 * version, such as one a close-out retired.
 *
 * Medplum reads the version from inside the quotes, so the value must be the full weak ETag. A bare
 * versionId makes Medplum ignore the precondition.
 *
 * @param list - Pregnancy record `List` about to be written
 * @returns `W/"<versionId>"`, or `undefined` when the list has no `meta.versionId`
 */
export function getPregnancyRecordListIfMatch(list: List): string | undefined {
  const versionId = list.meta?.versionId;
  return versionId ? `W/"${versionId}"` : undefined;
}
